import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, makeStyles } from '@material-ui/core';
import { Document, Page, pdfjs } from 'react-pdf';
import { Loader, usePrintDispatch } from '@mummssoftware/common-ui';
import type {
  EmailInstructions,
  FaxInstructions,
} from '@mummssoftware/common-ui';

import defaults from '../../translations/en.json';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const useToolbarStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
}));

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  viewer: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  pageWrap: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
}));

export type PrintFaxToolbarProps = {
  getPrintPromise: (
    faxInstructions: FaxInstructions | undefined,
    emailInstructions: EmailInstructions | undefined,
  ) => Promise<any>;
};

/**
 * Print, fax, and email via print-manifest (archived IDG PDF).
 */
export const PrintFaxToolbar = ({ getPrintPromise }: PrintFaxToolbarProps) => {
  const classes = useToolbarStyles();
  const [loading, setLoading] = useState(false);

  const { print, fax, email } = usePrintDispatch(
    getPrintPromise,
    () => setLoading(true),
    () => setLoading(false),
  );

  return (
    <>
      {loading &&
        createPortal(
          <Loader />,
          document.getElementById('portal-container') || document.body,
        )}
      <div className={classes.toolbar}>
        <Button color="primary" variant="contained" onClick={print}>
          {defaults.print}
        </Button>
        <Button color="primary" variant="outlined" onClick={fax}>
          {defaults.fax}
        </Button>
        <Button color="primary" variant="outlined" onClick={email}>
          Email
        </Button>
      </div>
    </>
  );
};

export type IdgPdfBlobViewerProps = {
  file: Blob;
  maxPageWidth?: number;
  getPrintPromise: (
    faxInstructions: FaxInstructions | undefined,
    emailInstructions: EmailInstructions | undefined,
  ) => Promise<any>;
};

export const IdgPdfBlobViewer = ({
  file,
  maxPageWidth,
  getPrintPromise,
}: IdgPdfBlobViewerProps) => {
  const classes = useStyles();
  const [numPages, setNumPages] = useState<number | null>(null);

  const pageWidth = useMemo(() => {
    if (maxPageWidth != null) return maxPageWidth;
    if (typeof window === 'undefined') return 640;
    return Math.min(880, window.innerWidth - 48);
  }, [maxPageWidth]);

  return (
    <div className={classes.root}>
      <PrintFaxToolbar getPrintPromise={getPrintPromise} />
      <div className={classes.viewer}>
        <Document
          file={file}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          loading={<span>{defaults.loadingPdf}</span>}
          error={<span>{defaults.pdfLoadError}</span>}
        >
          {numPages != null &&
            Array.from({ length: numPages }, (_, i) => (
              <div key={i + 1} className={classes.pageWrap}>
                <Page pageNumber={i + 1} width={pageWidth} />
              </div>
            ))}
        </Document>
      </div>
    </div>
  );
};
